import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  Autocomplete,
  TextField,
  CircularProgress,
  Box,
  Typography,
  Avatar,
  Alert,
} from '@mui/material'
import axios from 'axios'

import { useFieldFocus } from 'context/FieldFocusContext'

/**
 * CompanySearchAutocomplete Component
 * 
 * A React component that provides autocomplete functionality for company search
 * using The Companies API. Features debounced search, loading states, and error handling.
 * 
 * @param {Object} props - Component props
 * @param {Function} props.onChange - Callback when user types (receives string value)
 * @param {Function} props.onCompanySelect - Callback when company is selected (receives company object)
 * @param {Function} props.onCompanyLogoSelect - Callback when company is selected (receives logo URL string)
 * @param {string} props.placeholder - Placeholder text for the input
 * @param {string} props.value - Controlled value for the input
 * @param {boolean} props.disabled - Whether the input is disabled
 * @param {Object} props.sx - Additional styling for the component
 */
const CompanySearchAutocomplete = ({
  onChange,
  onCompanySelect,
  onCompanyLogoSelect,
  placeholder = 'Type to search companies...',
  value = '',
  disabled = false,
  sx = {},
  enableHelpText,
  helpText = "Company Search",
  id,
  name,
  onFocus,
  ...props
}) => {
  // State management
  const [options, setOptions] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [inputValue, setInputValue] = useState(value)
  const { handleFocus, handleBlur, registerHelpField } = useFieldFocus()
  const inputRef = useRef(null)
  const fieldId = id || name

  useEffect(() => {
    if (enableHelpText && fieldId) {
      registerHelpField(fieldId, helpText)
    }
  }, [enableHelpText, fieldId])

  /**
   * Fetch companies from The Companies API
   * @param {string} searchTerm - The search term to query
   * @returns {Promise<Array>} Array of company objects
   */
  const fetchCompanies = useCallback(async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      return []
    }

    try {
      setError(null)
      const response = await axios.get(
        'https://api.thecompaniesapi.com/v2/companies/by-name',
        {
          params: {
            name: searchTerm,
            size: 10, // Limit to 10 results for better performance
            simplified: false, // Get full company data
          },
          headers: {
            'Authorization': `Bearer ${process.env.REACT_APP_COMPANIES_API_KEY}`,
            'Content-Type': 'application/json',
          },
          timeout: 10000, // 10 second timeout
        }
      )

      return response.data?.companies || []
    } catch (err) {
      console.error('Error fetching companies:', err)
      
      // Handle different types of errors
      if (err.response?.status === 429) {
        setError('Rate limit exceeded. Please try again in a moment.')
      } else if (err.response?.status === 401) {
        setError('API authentication failed. Please check your API key.')
      } else if (err.code === 'ECONNABORTED') {
        setError('Request timeout. Please try again.')
      } else {
        setError('Failed to fetch companies. Please try again.')
      }
      
      return []
    }
  }, [])

  /**
   * Debounced search function
   * Delays API calls by 300ms to avoid excessive requests
   */
  useEffect(() => {
    const timeoutId = setTimeout(async () => {
      if (inputValue && inputValue.length >= 2) {
        setLoading(true)
        const companies = await fetchCompanies(inputValue)
        setOptions(companies)
        setLoading(false)
      } else {
        setOptions([])
      }
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [inputValue, fetchCompanies])

  /**
   * Handle input value changes (for typing)
   * @param {Event} event - The input change event
   * @param {string} newValue - The new input value
   */
  const handleInputChange = useCallback((event, newValue) => {
    setInputValue(newValue)
  }, [])

  /**
   * Handle company selection
   * @param {Event} event - The selection event
   * @param {Object|null} selectedCompany - The selected company object
   */
  const handleCompanySelect = useCallback((event, selectedCompany) => {
    if (selectedCompany) {
      const companyName = getCompanyName(selectedCompany)
      const logoUrl = getCompanyLogo(selectedCompany)

      onChange({
        name: companyName,
        logo: logoUrl,
        company: selectedCompany
      })
    }
  }, [onChange])

  /**
   * Get company display name
   * @param {Object} company - Company object from API
   * @returns {string} Display name for the company
   */
  const getCompanyName = (company) => {
    return company?.about?.name || company?.name || 'Unknown Company'
  }

  /**
   * Get company logo URL
   * @param {Object} company - Company object from API
   * @returns {string|null} Logo URL or null if not available
   */
  const getCompanyLogo = (company) => {
    return company?.assets?.logoSquare?.src || null
  }

  /**
   * Get company description/summary
   * @param {Object} company - Company object from API
   * @returns {string} Company description
   */
  const getCompanyDescription = (company) => {
    return company?.about?.description || 
           company?.about?.summary || 
           company?.description || 
           ''
  }

  return (
    <Box sx={{ width: '100%', ...sx }}>
      <Autocomplete
        freeSolo
        options={options}
        loading={loading}
        disabled={disabled}
        value={inputValue}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        onChange={handleCompanySelect}
        getOptionLabel={(option) => {
          if (typeof option === 'string') return option
          return getCompanyName(option)
        }}
        filterOptions={(x) => x} // Disable client-side filtering since we're using server-side search
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder}
            disabled={disabled}
            onFocus={(e) => {
              handleFocus(fieldId, e.target)
            }}
            onBlur={handleBlur}
            onMouseEnter={(e) => handleFocus(fieldId, e.currentTarget)}
            onMouseLeave={() => handleBlur()}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            // Accessibility attributes
            inputProps={{
              ...params.inputProps,
              'aria-label': 'Company Search',
              'aria-describedby': error ? 'company-search-error' : undefined,
            }}
            inputRef={inputRef}
          />
        )}
        renderOption={(props, option) => (
          <Box
            component="li"
            {...props}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              py: 1,
            }}
          >
            {/* Company Logo */}
            <Avatar
              src={getCompanyLogo(option)}
              alt={`${getCompanyName(option)} logo`}
              sx={{
                width: 32,
                height: 32,
                fontSize: '0.875rem',
                bgcolor: 'primary.main',
              }}
            >
              {getCompanyName(option).charAt(0).toUpperCase()}
            </Avatar>
            
            {/* Company Info */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body1" noWrap>
                {getCompanyName(option)}
              </Typography>
              {getCompanyDescription(option) && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  noWrap
                  sx={{ fontSize: '0.75rem' }}
                >
                  {getCompanyDescription(option)}
                </Typography>
              )}
            </Box>
          </Box>
        )}
        noOptionsText={
          inputValue.length < 2
            ? 'Type at least 2 characters to search'
            : loading
            ? 'Searching...'
            : 'No companies found'
        }
        // Keyboard navigation support
        autoHighlight
        selectOnFocus
        clearOnBlur
        handleHomeEndKeys
        {...props}
      />
      
      {/* Error Display */}
      {error && (
        <Alert
          severity="error"
          sx={{ mt: 1 }}
          id="company-search-error"
          role="alert"
          aria-live="polite"
        >
          {error}
        </Alert>
      )}
    </Box>
  )
}

export default CompanySearchAutocomplete
